import React from 'react';

import { Box, Stack, Typography, useTheme } from '@mui/material';
import { CreditCardIcon } from 'lucide-react';

import type { PaymentMethodRow } from 'hooks/api/usePaymentMethods';
import amex from 'assets/images/amex.svg';
import visa from 'assets/images/visa.svg';
import mastercardLogo from 'assets/images/mastercard.svg';

// Map the Stripe brand string → its logo. Discover/PayPal have no asset yet →
// fall back to a generic card glyph.
const brandLogo = (brand?: string | null): string | null => {
  const key = (brand || '').toLowerCase();
  if (key === 'visa') return visa;
  if (key === 'amex' || key === 'american express') return amex;
  if (key === 'mastercard' || key === 'master card') return mastercardLogo;
  return null;
};

// "Visa •••• 9385" — brand capitalized, last4 masked.
const formatCard = (method: PaymentMethodRow): string => {
  const brand = method.brand
    ? method.brand.charAt(0).toUpperCase() + method.brand.slice(1)
    : 'Card';
  return `${brand} •••• ${method.last4}`;
};

/**
 * Shared payment-method row preview: brand-logo avatar + nickname (title) +
 * masked "Brand •••• last4". Single source so the saved-card display is
 * identical across the checkout row, the change-payment picker, the invoices
 * table cell and the Settings → Payment methods list (each of which used to
 * hand-roll it — one with the logo, the others with a generic card icon).
 */
export const PaymentMethodPreview = ({
  method,
  caption,
}: {
  method: PaymentMethodRow;
  /**
   * Optional tertiary line under the card identity — e.g. "Expires 12/26" on
   * the Settings → Payment methods management list, which is the one surface
   * where the expiry is the thing you came to check. Omitted everywhere else.
   */
  caption?: string;
}) => {
  const theme = useTheme();
  const logo = brandLogo(method.brand);

  // The card's NICKNAME, when it has one. Callers often set `label` to the card
  // identity itself (e.g. "VISA •••• 4242"), which would just duplicate the
  // identity line — treat any label containing the last4 as "not a real
  // nickname".
  const nickname =
    method.label && method.last4 && method.label.includes(method.last4)
      ? null
      : method.label;

  // With no nickname, the IDENTITY is the title. This used to fall back to the
  // literal string "Card", which stacked a meaningless first line above the only
  // line carrying information — and since the API never returns a nickname
  // (`PaymentMethod` has no `label`; every caller synthesizes it, and the ones
  // that synthesize it from brand+last4 are caught just above), that fallback
  // was what actually rendered on every surface.
  const identity = formatCard(method);
  const title = nickname || identity;
  const secondary = nickname ? identity : caption;
  const tertiary = nickname ? caption : undefined;

  return (
    <Stack direction="row" alignItems="center" spacing={3} minWidth={0}>
      <Box
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: `1px solid ${theme.palette.primary[100]}`,
          color: theme.palette.text.secondary,
        }}
      >
        {logo ? (
          <Box
            component="img"
            src={logo}
            alt={method.brand ?? 'Card'}
            sx={{ width: 22, height: 'auto' }}
          />
        ) : (
          <CreditCardIcon size={20} strokeWidth={1} />
        )}
      </Box>
      <Stack minWidth={0}>
        <Typography variant="body2" noWrap>
          {title}
        </Typography>
        {secondary && (
          <Typography variant="body3" color="text.secondary" noWrap>
            {secondary}
          </Typography>
        )}
        {tertiary && (
          <Typography variant="body3" color="text.secondary" noWrap>
            {tertiary}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

export default PaymentMethodPreview;
